/**
 * CLE-2811 — how the run page keeps up with a pass in flight without re-downloading the whole run.
 *
 * It used to refetch the full run (every recommendation, attribution and message) every 4 s while a pass
 * ran. Now it polls the slim `GET /runs/:id/progress` every 1.5 s — status, clocks, the worker's live
 * progress, queue position — and refetches the full run only when something the full run shows has moved
 * (`shouldRefetchRun`, including the facts-first `interimAt` stamp), plus a 10-s safety net. When the pass ends, the change of status is what brings
 * the result in: one final full refetch, and the slim poll stops with it.
 *
 * Pure, so the rules are tested without a store or a DOM.
 */

import {
  DraftRunStatus,
  IDraftRunDetail,
  IDraftRunProgress,
} from "store/queries/contractModelDrafts";

import { isProcessing } from "./utils";

/** The slim progress poll while a pass is in flight. */
export const PROGRESS_POLL_MS = 1500;

/** The full run's safety-net refetch while a pass is in flight — at most this often on its own. */
export const RUN_SAFETY_REFETCH_MS = 10_000;

/** What decides "the full run has changed": the fields the slim response shares with it. */
export interface IRunChangeKey {
  status: DraftRunStatus;
  latestVersionId: string | null;
  /** Null = not known on this side; an unknown count never counts as a change. */
  messageCount: number | null;
  versionCount: number | null;
  /**
   * CLE-2811 (facts first) — the run's `interimAt`. Null = not set; undefined = not known on this side (a
   * response without the field), which never counts as a change.
   */
  interimAt?: string | null;
}

/** A string stamp, null for "not set", undefined when the field is absent or malformed. */
const stampOf = (v: unknown): string | null | undefined => {
  if (typeof v === "string") return v;
  if (v === null) return null;
  return undefined;
};

export const progressChangeKey = (
  p: Pick<
    IDraftRunProgress,
    "status" | "latestVersionId" | "messageCount" | "versionCount"
  > & { interimAt?: string | null }
): IRunChangeKey => ({
  status: p.status,
  latestVersionId: p.latestVersionId ?? null,
  messageCount: typeof p.messageCount === "number" ? p.messageCount : null,
  versionCount: typeof p.versionCount === "number" ? p.versionCount : null,
  interimAt: stampOf(p.interimAt),
});

/**
 * The baseline before the first slim response of a pass: what the full run says. Its counts are left
 * unknown on purpose — they come from a different query, and a definitional mismatch between the two
 * (all messages vs hers only, say) would read as a change on every tick and refetch the run every 1.5 s.
 */
export const runChangeKey = (
  run: Pick<IDraftRunDetail, "status" | "latestVersionId"> & {
    interimAt?: string | null;
  }
): IRunChangeKey => ({
  status: run.status,
  latestVersionId: run.latestVersionId ?? null,
  messageCount: null,
  versionCount: null,
  interimAt: stampOf(run.interimAt),
});

const countMoved = (a: number | null, b: number | null): boolean =>
  a != null && b != null && a !== b;

const stampMoved = (
  a: string | null | undefined,
  b: string | null | undefined
): boolean => a !== undefined && b !== undefined && a !== b;

/**
 * True when the slim response says the full run has moved since `prev`: a new status (queued → running
 * → worker_returned → completed, or a chained pass queued again), a new latest version, a new message
 * (her queued one, or the AI's reply), a new version, or — CLE-2811 facts first — a new `interimAt`:
 * the first pass's contract facts were just saved as real recommendations while it keeps reading the
 * schedules. With no `prev` there is nothing to compare.
 */
export const shouldRefetchRun = (
  prev: IRunChangeKey | null,
  next: IRunChangeKey
): boolean =>
  !!prev &&
  (prev.status !== next.status ||
    prev.latestVersionId !== next.latestVersionId ||
    countMoved(prev.messageCount, next.messageCount) ||
    countMoved(prev.versionCount, next.versionCount) ||
    stampMoved(prev.interimAt, next.interimAt));

/**
 * The slim response to use, or null. RTK Query keeps a hook's last data across `skip` and serves a cached
 * entry again on re-subscribe, so after a Regenerate the first thing the hook returns can be the PREVIOUS
 * pass's final "completed" tick. Only a response fetched since this pass started (`sinceMs`) counts.
 */
export const freshProgress = (
  data: IDraftRunProgress | undefined,
  fulfilledAtMs: number | undefined,
  sinceMs: number | null
): IDraftRunProgress | null =>
  data && fulfilledAtMs != null && sinceMs != null && fulfilledAtMs >= sinceMs
    ? data
    : null;

/** The run fields the slim poll keeps live, and the one it adds. */
export type LiveRunBase = Pick<
  IDraftRunDetail,
  "id" | "status" | "startedAt" | "updatedAt" | "progress" | "typicalDurationMs"
>;

/**
 * The run as the progress strip and the conversation's live reply should see it: the full run, with the
 * slim response's status, clocks, live progress and queue position laid over it.
 *
 * Only while BOTH say a pass is in flight. Leaving a processing status is taken from the full run alone:
 * it is the one carrying the result, so the strip and the live reply stay up until the recommendations
 * and the stored reply are actually on the page — never a blank beat in between. And a full run that
 * has already settled (a cancel's response, say) is never overruled by the slim poll's last tick.
 */
export const liveRunOf = <R extends LiveRunBase>(
  run: R,
  slim: IDraftRunProgress | null
): R & { queuePosition: number | null } => {
  if (
    !slim ||
    slim.id !== run.id ||
    !isProcessing(run.status) ||
    !isProcessing(slim.status)
  )
    return { ...run, queuePosition: null };
  return {
    ...run,
    status: slim.status,
    startedAt: slim.startedAt,
    updatedAt: slim.updatedAt,
    progress: slim.progress,
    typicalDurationMs: slim.typicalDurationMs,
    queuePosition:
      typeof slim.queuePosition === "number" ? slim.queuePosition : null,
  };
};
